import { createSlice } from '@reduxjs/toolkit';
import { serializeTopic } from './chatTopic';
import AssistantMessageStatus from './assistantMessageStatus';
import isEqual from 'lodash/isEqual';
/**
{
  chatStateByTopic: {
    "TOP_LEVEL": {
      "chatHistory": [],
      "additionalSelectedDocs": [],
    },
    "FIELD:123:field1": {
      "chatHistory": [],
      "additionalSelectedDocs": [],
    },
    "DOCUMENT:123:doc1": {
      "chatHistory": [],
      "additionalSelectedDocs": [],
    },
    "FUND:123": {
      "chatHistory": [],
      "additionalSelectedDocs": [],
    },
  },
  currentChatTopic: {
    topicType: null,
    topicId: null,
    fieldName: null,
  },
  isChatCollapsed: false,
}
*/

const initialState = {
  currentChatTopic: null,
  chatStateByTopic: {},
  isChatCollapsed: false,
};

const addChatHistoryForTopicIfNotExists = (state, topic) => {
  const serializedTopic = serializeTopic(topic);
  if (!state.chatStateByTopic[serializedTopic]) {
    state.chatStateByTopic[serializedTopic] = {
      chatHistory: [],
      additionalSelectedDocs: [],
    };
  }
};
const appendUserMessageToChatHistory = (chatObject, message) => {
  const chatHistory = chatObject?.chatHistory ?? [];
  const newChatHistory = [
    ...chatHistory,
    {
      role: 'user',
      content: message,
    },
  ];
  chatObject.chatHistory = newChatHistory;
};

const appendPendingAssistantMessageToChatHistory = (chatObject) => {
  const chatHistory = chatObject?.chatHistory ?? [];
  const newChatHistory = [
    ...chatHistory,
    {
      role: 'assistant',
      status: AssistantMessageStatus.PENDING,
      content: '',
    },
  ];
  chatObject.chatHistory = newChatHistory;
};

const getCurrentChatState = (state) => {
  const currentTopic = state.currentChatTopic;
  if (!currentTopic) {
    return null;
  }
  const serializedTopic = serializeTopic(currentTopic);
  return state.chatStateByTopic[serializedTopic];
};

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    submitUserMessage: (state, { payload: { message } }) => {
      const chatStateObject = getCurrentChatState(state);
      appendUserMessageToChatHistory(chatStateObject, message);
      appendPendingAssistantMessageToChatHistory(chatStateObject);
    },
    contributeChunkToLatestAssistantMessage: (
      state,
      { payload: { chunk } }
    ) => {
      const chatStateObject = getCurrentChatState(state);
      if (!chatStateObject) {
        return;
      }
      const latestAssistantMessage =
        chatStateObject.chatHistory[chatStateObject.chatHistory.length - 1];
      latestAssistantMessage.content += chunk;
    },
    completeLatestAssistantMessage: (state, { payload: { response = '' } }) => {
      const chatStateObject = getCurrentChatState(state);
      if (!chatStateObject) {
        return;
      }
      const latestAssistantMessage =
        chatStateObject.chatHistory[chatStateObject.chatHistory.length - 1];
      if (response) {
        latestAssistantMessage.content = response;
      }
      latestAssistantMessage.status = AssistantMessageStatus.COMPLETED;
    },
    errorLatestAssistantMessage: (state) => {
      const chatStateObject = getCurrentChatState(state);
      if (!chatStateObject) {
        return;
      }
      const latestAssistantMessage =
        chatStateObject.chatHistory[chatStateObject.chatHistory.length - 1];
      latestAssistantMessage.status = AssistantMessageStatus.ERROR;
    },
    setAdditionalSelectedDocs: (state, { payload: { docPaths } }) => {
      const chatStateObject = getCurrentChatState(state);
      if (!chatStateObject) {
        return;
      }
      chatStateObject.additionalSelectedDocs = docPaths;
    },
    clearChatForTopic: (state) => {
      const chatStateObject = getCurrentChatState(state);
      if (!chatStateObject) {
        return;
      }
      chatStateObject.chatHistory = [];
      chatStateObject.additionalSelectedDocs = [];
    },
    setCurrentChatTopic: (
      state,
      {
        payload: {
          topicType,
          topicId,
          fieldName,
          listItemId,
          listItemType,
          uiConfigSectionName,
        },
      }
    ) => {
      addChatHistoryForTopicIfNotExists(state, {
        topicType,
        topicId,
        fieldName,
        listItemId,
        listItemType,
        uiConfigSectionName,
      });
      state.isChatCollapsed = false;
      state.currentChatTopic = {
        topicType,
        topicId,
        fieldName,
        listItemId,
        listItemType,
        uiConfigSectionName,
      };
    },
    clearCurrentChatTopic: (state) => {
      state.currentChatTopic = null;
    },
    addActionsToLatestAssistantMessage: (state, { payload: { actions } }) => {
      const chatStateObject = getCurrentChatState(state);
      if (!chatStateObject) {
        return;
      }
      const latestAssistantMessage =
        chatStateObject.chatHistory[chatStateObject.chatHistory.length - 1];
      if (!latestAssistantMessage.actions) {
        latestAssistantMessage.actions = [];
      }
      for (const action of actions) {
        if (!latestAssistantMessage.actions.some((a) => isEqual(a, action))) {
          latestAssistantMessage.actions.push(action);
        }
      }
      if (
        latestAssistantMessage.content?.charAt(
          latestAssistantMessage.content.length - 1
        ) !== '\n'
      ) {
        latestAssistantMessage.content += '\n';
      }
    },
    setIsChatCollapsed: (state, { payload: isCollapsed = false }) => {
      state.isChatCollapsed = isCollapsed;
    },
  },
});

export const {
  submitUserMessage,
  contributeChunkToLatestAssistantMessage,
  completeLatestAssistantMessage,
  errorLatestAssistantMessage,
  setAdditionalSelectedDocs,
  clearChatForTopic,
  setCurrentChatTopic,
  clearCurrentChatTopic,
  addActionsToLatestAssistantMessage,
  setIsChatCollapsed,
} = aiChatSlice.actions;
export default aiChatSlice.reducer;
